// File: src/lib/graphql/queries/attendance.ts
// Purpose: Attendance Management GraphQL Queries and Mutations
// VIBE - Attendance API

import { gql } from 'graphql-request'
import { executeGraphQL } from '../client'

// ============================================
// TYPES
// ============================================

export interface DeviceInfo {
  deviceId?: string
  deviceType?: string
  ipAddress?: string
}

export interface Location {
  latitude?: number
  longitude?: number
  address?: string
}

export interface AttendanceCheck {
  time?: string
  method?: string
  selfie?: string
  deviceInfo?: DeviceInfo
  location?: Location
}

export interface Break {
  startTime?: string
  endTime?: string
  duration?: number
  type?: string
  reason?: string
}

export interface Employee {
  azureId?: string
  azureTenantId?: string
  azureEmail?: string
  azureDisplayName?: string
  ssoEnabled?: boolean
  lastAzureLogin?: string
  azureGroups?: string[]
  _id: string
  firstName: string
  lastName: string
  email: string
  countryCode?: string
  mobile_number?: string
  languages?: string[]
  joining_date?: string
  skills?: string[]
  designation?: string
  department?: string
  shift?: string
  hourlyRate?: number
  overtimeRate?: number
  is_active: boolean
  is_deleted?: boolean
  createdAt: string
  updatedAt: string
}

export interface Attendance {
  id: string
  date: string
  totalWorkHours?: number
  totalBreakHours?: number
  netWorkHours?: number
  overtime?: number
  earlyCheckIn?: boolean
  lateCheckIn?: boolean
  earlyCheckOut?: boolean
  lateCheckOut?: boolean
  status?: string
  remarks?: string
  approvalStatus?: string
  approvalRemarks?: string
  createdAt: string
  updatedAt: string
  employee?: Employee
  checkIn?: AttendanceCheck
  checkOut?: AttendanceCheck
  breaks?: Break[]
}

export interface AttendanceCheckInInput {
  employeeId: string
  latitude?: number | null
  longitude?: number | null
  deviceId?: string | null
  deviceType?: string | null
  ipAddress?: string | null
}

export interface AttendanceCheckOutInput {
  employeeId: string
  latitude?: number | null
  longitude?: number | null
  deviceId?: string | null
  deviceType?: string | null
  ipAddress?: string | null
}

export interface AttendanceFilterInput {
  startDate?: string | null
  endDate?: string | null
  status?: string | null
  approvalStatus?: string | null
  limit?: number | null
}

export interface ApproveAttendanceInput {
  id: string
  remarks?: string | null
}

export interface RejectAttendanceInput {
  id: string
  remarks: string
}

export interface RegularizeAttendanceInput {
  employeeId: string
  date: string
  checkIn: string
  checkOut: string
  reason: string
}

export interface BulkApproveAttendanceInput {
  ids: string[]
}

export interface UpdateAttendanceFullInput {
  checkIn?: { time: string; method?: string } | null
  checkOut?: { time: string; method?: string } | null
  status?: string | null
  remarks?: string | null
}

export interface AttendanceStatusCountsInput {
  startDate: string
  endDate: string
}

export interface AttendanceStatusCounts {
  present: number
  absent: number
  halfDay: number
  holiday: number
  weekend: number
  leave: number
  workFromHome: number
  total: number
}


// ============================================
// QUERIES
// ============================================

export const TODAY_ATTENDANCE_QUERY = gql`
  query TodayAttendance {
    todayAttendance {
      id
      date
      totalWorkHours
      totalBreakHours
      netWorkHours
      overtime
      earlyCheckIn
      lateCheckIn
      earlyCheckOut
      lateCheckOut
      status
      remarks
      approvalStatus
      approvalRemarks
      createdAt
      updatedAt
      checkIn {
        time
        method
        selfie
      }
      checkOut {
        time
        method
        selfie
      }
    }
  }
`

export const ATTENDANCES_QUERY = gql`
  query Attendances($filter: AttendanceFilterInput, $limit: Int) {
    attendances(filter: $filter, limit: $limit) {
      id
      date
      totalWorkHours
      totalBreakHours
      netWorkHours
      overtime
      earlyCheckIn
      lateCheckIn
      earlyCheckOut
      lateCheckOut
      status
      remarks
      approvalStatus
      approvalRemarks
      createdAt
      updatedAt
      employee {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        hourlyRate
        overtimeRate
        is_active
        is_deleted
        createdAt
        updatedAt
      }
      checkIn {
        time
        selfie
        deviceInfo {
          deviceId
          deviceType
          ipAddress
        }
        method
        location {
          latitude
          longitude
          address
        }
      }
      checkOut {
        time
        method
        selfie
        location {
          latitude
          longitude
          address
        }
        deviceInfo {
          deviceId
          deviceType
          ipAddress
        }
      }
      breaks {
        startTime
        endTime
        duration
        type
        reason
      }
    }
  }
`

export const ATTENDANCE_STATUS_COUNTS_QUERY = gql`
  query AttendanceStatusCounts($input: AttendanceStatusCountsInput!) {
    attendanceStatusCounts(input: $input) {
      present
      absent
      halfDay
      holiday
      weekend
      leave
      workFromHome
      total
    }
  }
`

// ============================================
// MUTATIONS
// ============================================

export const ATTENDANCE_CHECK_IN_MUTATION = gql`
  mutation AttendanceCheckIn(
    $employeeId: ID!
    $latitude: Float
    $longitude: Float
    $deviceId: String
    $deviceType: String
    $ipAddress: String
  ) {
    attendanceCheckIn(
      input: {
        employeeId: $employeeId
        latitude: $latitude
        longitude: $longitude
        deviceId: $deviceId
        deviceType: $deviceType
        ipAddress: $ipAddress
      }
    ) {
      id
      date
      totalWorkHours
      totalBreakHours
      netWorkHours
      overtime
      earlyCheckIn
      lateCheckIn
      earlyCheckOut
      lateCheckOut
      status
      remarks
      approvalStatus
      approvalRemarks
      createdAt
      updatedAt
      checkIn {
        time
        method
        selfie
      }
      checkOut {
        time
        method
        selfie
      }
    }
  }
`

export const ATTENDANCE_CHECK_OUT_MUTATION = gql`
  mutation AttendanceCheckOut(
    $employeeId: ID!
    $latitude: Float
    $longitude: Float
    $deviceId: String
    $deviceType: String
    $ipAddress: String
  ) {
    attendanceCheckOut(
      input: {
        employeeId: $employeeId
        latitude: $latitude
        longitude: $longitude
        deviceId: $deviceId
        deviceType: $deviceType
        ipAddress: $ipAddress
      }
    ) {
      id
      date
      totalWorkHours
      totalBreakHours
      netWorkHours
      overtime
      earlyCheckIn
      lateCheckIn
      earlyCheckOut
      lateCheckOut
      status
      remarks
      approvalStatus
      approvalRemarks
      createdAt
      updatedAt
      checkIn {
        time
        method
        selfie
      }
      checkOut {
        time
        method
        selfie
      }
    }
  }
`

export const APPROVE_ATTENDANCE_MUTATION = gql`
  mutation ApproveAttendance($id: ID!, $remarks: String) {
    approveAttendance(id: $id, remarks: $remarks) {
      id
      date
      remarks
      approvalStatus
      approvalRemarks
      createdAt
      updatedAt
      employee {
        _id
        firstName
        lastName
        email
      }
    }
  }
`

export const REJECT_ATTENDANCE_MUTATION = gql`
  mutation RejectAttendance($id: ID!, $remarks: String!) {
    rejectAttendance(id: $id, remarks: $remarks) {
      id
      date
      employee {
        _id
        firstName
        lastName
        email
      }
    }
  }
`

export const REGULARIZE_ATTENDANCE_MUTATION = gql`
  mutation RegularizeAttendance(
    $employeeId: ID!
    $date: String!
    $checkIn: String!
    $checkOut: String!
    $reason: String!
  ) {
    regularizeAttendance(
      employeeId: $employeeId
      date: $date
      checkIn: $checkIn
      checkOut: $checkOut
      reason: $reason
    ) {
      id
      date
      employee {
        _id
        firstName
        lastName
        email
      }
      checkIn {
        time
        method
        selfie
      }
      checkOut {
        time
        method
        selfie
      }
    }
  }
`

export const BULK_APPROVE_ATTENDANCE_MUTATION = gql`
  mutation BulkApproveAttendance($ids: [ID!]!) {
    bulkApproveAttendance(ids: $ids) {
      id
      date
      totalWorkHours
      totalBreakHours
      netWorkHours
      overtime
      earlyCheckIn
      lateCheckIn
      earlyCheckOut
      lateCheckOut
      status
      remarks
      approvalStatus
      approvalRemarks
      createdAt
      updatedAt
      employee {
        _id
        firstName
        lastName
        email
      }
    }
  }
`

export const UPDATE_ATTENDANCE_FULL_MUTATION = gql`
  mutation UpdateAttendanceFull(
    $id: ID!
    $checkInTime: String!
    $checkInMethod: CheckInMethod
    $checkOutTime: String!
    $checkOutMethod: CheckInMethod
    $status: AttendanceStatus
    $remarks: String
  ) {
    updateAttendanceFull(
      id: $id
      input: {
        checkIn: { time: $checkInTime, method: $checkInMethod }
        checkOut: { time: $checkOutTime, method: $checkOutMethod }
        status: $status
        remarks: $remarks
      }
    ) {
      id
      date
      totalWorkHours
      totalBreakHours
      netWorkHours
      overtime
      earlyCheckIn
      lateCheckIn
      earlyCheckOut
      lateCheckOut
      status
      remarks
      approvalStatus
      approvalRemarks
      createdAt
      updatedAt
      checkIn {
        time
        method
      }
      checkOut {
        time
        method
      }
      employee {
        _id
        firstName
        lastName
        email
      }
    }
  }
`


// ============================================
// API CLASS
// ============================================

export class AttendanceAPI {
  // Query: Get Today's Attendance
  static async getTodayAttendance(): Promise<Attendance | null> {
    try {
      const { todayAttendance } = await executeGraphQL<{ todayAttendance: Attendance }>(
        TODAY_ATTENDANCE_QUERY
      )
      return todayAttendance
    } catch (error) {
      console.error('Failed to fetch today attendance:', error)
      return null
    }
  }

  // Query: Get Attendances with Filter
  static async getAttendances(filter?: AttendanceFilterInput): Promise<Attendance[]> {
    try {
      const { limit, ...filterWithoutLimit } = filter || {}
      const { attendances } = await executeGraphQL<{ attendances: Attendance[] }>(
        ATTENDANCES_QUERY,
        {
          filter: filterWithoutLimit || { startDate: null, endDate: null, status: null, approvalStatus: null },
          limit: limit || null
        }
      )
      return attendances || []
    } catch (error) {
      console.error('Failed to fetch attendances:', error)
      return []
    }
  }

  // Mutation: Attendance Check In
  static async checkIn(input: AttendanceCheckInInput): Promise<Attendance | null> {
    try {
      if (!input.employeeId) {
        throw new Error('Employee ID is required for check-in')
      }
      const { attendanceCheckIn } = await executeGraphQL<{ attendanceCheckIn: Attendance }>(
        ATTENDANCE_CHECK_IN_MUTATION,
        {
          employeeId: input.employeeId,
          latitude: input.latitude || null,
          longitude: input.longitude || null,
          deviceId: input.deviceId || null,
          deviceType: input.deviceType || null,
          ipAddress: input.ipAddress || null
        }
      )
      return attendanceCheckIn
    } catch (error: any) {
      console.error('AttendanceAPI.checkIn failed:', error)
      console.log('Error will be re-thrown to dashboard')
      throw error // Re-throw to handle in UI
    }
  }

  // Mutation: Attendance Check Out
  static async checkOut(input: AttendanceCheckOutInput): Promise<Attendance | null> {
    try {
      if (!input.employeeId) {
        throw new Error('Employee ID is required for check-out')
      }
      const { attendanceCheckOut } = await executeGraphQL<{ attendanceCheckOut: Attendance }>(
        ATTENDANCE_CHECK_OUT_MUTATION,
        {
          employeeId: input.employeeId,
          latitude: input.latitude || null,
          longitude: input.longitude || null,
          deviceId: input.deviceId || null,
          deviceType: input.deviceType || null,
          ipAddress: input.ipAddress || null
        }
      )
      return attendanceCheckOut
    } catch (error: any) {
      console.error('AttendanceAPI.checkOut failed:', error)
      console.log('Error will be re-thrown to dashboard')
      throw error // Re-throw to handle in UI
    }
  }

  // Mutation: Approve Attendance
  static async approveAttendance(input: ApproveAttendanceInput): Promise<Attendance | null> {
    try {
      if (!input.id) {
        throw new Error('Attendance ID is required for approval')
      }
      const { approveAttendance } = await executeGraphQL<{ approveAttendance: Attendance }>(
        APPROVE_ATTENDANCE_MUTATION,
        {
          id: input.id,
          remarks: input.remarks || null
        }
      )
      return approveAttendance
    } catch (error: any) {
      console.error('AttendanceAPI.approveAttendance failed:', error)
      throw error // Re-throw to handle in UI
    }
  }

  // Mutation: Reject Attendance
  static async rejectAttendance(input: RejectAttendanceInput): Promise<Attendance | null> {
    try {
      if (!input.id || !input.remarks) {
        throw new Error('Attendance ID and remarks are required for rejection')
      }
      const { rejectAttendance } = await executeGraphQL<{ rejectAttendance: Attendance }>(
        REJECT_ATTENDANCE_MUTATION,
        {
          id: input.id,
          remarks: input.remarks
        }
      )
      return rejectAttendance
    } catch (error: any) {
      console.error('AttendanceAPI.rejectAttendance failed:', error)
      throw error // Re-throw to handle in UI
    }
  }

  // Mutation: Regularize Attendance
  static async regularizeAttendance(input: RegularizeAttendanceInput): Promise<Attendance | null> {
    try {
      if (!input.employeeId || !input.date || !input.checkIn || !input.checkOut || !input.reason) {
        throw new Error('All fields are required for regularization')
      }
      const { regularizeAttendance } = await executeGraphQL<{ regularizeAttendance: Attendance }>(
        REGULARIZE_ATTENDANCE_MUTATION,
        {
          employeeId: input.employeeId,
          date: input.date,
          checkIn: input.checkIn,
          checkOut: input.checkOut,
          reason: input.reason
        }
      )
      return regularizeAttendance
    } catch (error: any) {
      console.error('AttendanceAPI.regularizeAttendance failed:', error)
      throw error // Re-throw to handle in UI
    }
  }

  // Mutation: Bulk Approve Attendance
  static async bulkApproveAttendance(input: BulkApproveAttendanceInput): Promise<Attendance[]> {
    try {
      if (!input.ids || input.ids.length === 0) {
        throw new Error('At least one attendance ID is required for bulk approval')
      }
      const { bulkApproveAttendance } = await executeGraphQL<{ bulkApproveAttendance: Attendance[] }>(
        BULK_APPROVE_ATTENDANCE_MUTATION,
        {
          ids: input.ids
        }
      )
      return bulkApproveAttendance || []
    } catch (error: any) {
      console.error('AttendanceAPI.bulkApproveAttendance failed:', error)
      throw error // Re-throw to handle in UI
    }
  }

  // Mutation: Update Attendance Full (for inline editing check-in/check-out)
  static async updateAttendanceFull(
    id: string,
    input: UpdateAttendanceFullInput
  ): Promise<Attendance | null> {
    try {
      if (!id) {
        throw new Error('Attendance ID is required for update')
      }
      const { updateAttendanceFull } = await executeGraphQL<{ updateAttendanceFull: Attendance }>(
        UPDATE_ATTENDANCE_FULL_MUTATION,
        {
          id,
          checkInTime: input.checkIn?.time || '',
          checkInMethod: input.checkIn?.method || 'manual',
          checkOutTime: input.checkOut?.time || '',
          checkOutMethod: input.checkOut?.method || 'manual',
          status: input.status,
          remarks: input.remarks
        }
      )
      return updateAttendanceFull
    } catch (error: any) {
      console.error('AttendanceAPI.updateAttendanceFull failed:', error)
      throw error // Re-throw to handle in UI
    }
  }

  // Query: Get Attendance Status Counts
  static async getAttendanceStatusCounts(input: AttendanceStatusCountsInput): Promise<AttendanceStatusCounts | null> {
    try {
      if (!input.startDate || !input.endDate) {
        throw new Error('Start date and end date are required for status counts')
      }
      const { attendanceStatusCounts } = await executeGraphQL<{ attendanceStatusCounts: AttendanceStatusCounts }>(
        ATTENDANCE_STATUS_COUNTS_QUERY,
        { input }
      )
      return attendanceStatusCounts
    } catch (error: any) {
      console.error('AttendanceAPI.getAttendanceStatusCounts failed:', error)
      return null
    }
  }

}

export default AttendanceAPI